import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { ModalService } from '../../services/modal.service';
import { AlertService } from '../../services/alert.service';
import { StudentProfileService } from '../../../api/services/student-profile.service';
import { StudentProfile } from '../../../api/models/student-profile';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import {
  isAiImportable,
  exceedsSizeMb,
  willImport,
  actionLabelKey,
  type ImportSections,
} from './cv-upload-import.logic';

const DEFAULT_ACCEPT =
  '.txt,.doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png,.ppt,.pptx,.mpg,.mp4,.ogg';

export interface CvUploadImportResult {
  uploaded: boolean;
  imported: boolean;
  profile?: StudentProfile;
}

/**
 * Self-contained CV upload + AI-import Formly field. One file input (any format);
 * the AI-import block is always visible but disabled until a PDF is chosen. On the
 * primary action it always uploads the raw file, and — for a PDF with the toggle on
 * and at least one section — also runs the Mistral AI import (after a confirm).
 * Emits `props.onComplete(result)` once the action finishes.
 *
 * Usage:
 * ```
 * {
 *   key: 'cv',
 *   type: 'cv-upload-import',
 *   props: {
 *     profileId: '...',                 // required
 *     accept?: string,                  // default: full format list
 *     maxSizeMb?: number,               // default: 10
 *     secondaryLabel?: string,          // optional cancel/back button (e.g. modal hosts)
 *     onSecondary?: () => void,         // handler for the secondary button
 *     onComplete?: (r: CvUploadImportResult) => void,
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-formly-cv-upload-import',
  standalone: true,
  imports: [MatIconModule, FontAwesomeModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [attr.data-field-id]="field.id" class="flex flex-col gap-3">
      @if (error()) {
        <div class="alert alert-danger"><p class="mb-0">{{ error() | translate }}</p></div>
      }

      <!-- File input (any format) -->
      <label class="relative flex flex-col items-center justify-center gap-1.5 px-5 py-7 border-2 border-dashed rounded-2xl cursor-pointer text-center text-mty-primary border-mty-primary/45 bg-mty-primary/5 hover:bg-mty-primary/10">
        <mat-icon style="font-size:32px;width:32px;height:32px;">cloud_upload</mat-icon>
        <span class="font-alternate text-sm font-medium">{{ 'Drop file or click to select' | translate }}</span>
        <span class="font-base text-xs text-mty-primary/70">{{ 'Any format is stored' | translate }} · {{ maxSizeMb }} MB</span>
        @if (fileName(); as name) {
          <span class="font-base text-xs font-medium">{{ name }}</span>
        }
        <input type="file" class="absolute inset-0 opacity-0 cursor-pointer"
               [attr.accept]="accept"
               (change)="onFileChange($event)" />
      </label>

      <!-- AI import block: always visible, disabled until a PDF/Word file -->
      <div [class.opacity-50]="!isImportable()" [class.pointer-events-none]="!isImportable()" class="border-t border-dashed pt-3">
        <label class="flex items-center gap-2 font-medium">
          <input type="checkbox" [checked]="importEnabled()" [disabled]="!isImportable()"
                 (change)="importEnabled.set($any($event.target).checked)" />
          {{ 'Import data with AI' | translate }}
          <span class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{{ 'PDF or Word' | translate }}</span>
        </label>

        <div class="mt-2 flex flex-col gap-1">
          <label class="flex items-center gap-2">
            <input type="checkbox" [checked]="sections().education" [disabled]="!isImportable() || !importEnabled()"
                   (change)="setSection('education', $any($event.target).checked)" />
            {{ 'Education' | translate }}
          </label>
          <label class="flex items-center gap-2">
            <input type="checkbox" [checked]="sections().workExperience" [disabled]="!isImportable() || !importEnabled()"
                   (change)="setSection('workExperience', $any($event.target).checked)" />
            {{ 'Work Experience' | translate }}
          </label>
          <label class="flex items-center gap-2">
            <input type="checkbox" [checked]="sections().languages" [disabled]="!isImportable() || !importEnabled()"
                   (change)="setSection('languages', $any($event.target).checked)" />
            {{ 'Languages' | translate }}
          </label>
        </div>

        @if (isImportable()) {
          <div class="alert alert-warning small mt-3">
            <strong>{{ 'Processed via Mistral AI.' | translate }}</strong>
            {{ 'Your file is read by Mistral AI to extract the selected sections.' | translate }}
            <a href="/pages/privacyverklaring" target="_blank" rel="noopener">{{ 'See our privacy terms' | translate }}</a>.
          </div>
        } @else if (fileName()) {
          <div class="alert alert-info small mt-3">
            {{ 'Automatic import only works with a PDF or Word file. Your CV will still be saved.' | translate }}
          </div>
        }
      </div>

      <div class="flex items-center justify-end gap-2">
        @if (secondaryLabel) {
          <button type="button" class="btn btn-outline-secondary" [disabled]="isProcessing()" (click)="onSecondary()">
            {{ secondaryLabel | translate }}
          </button>
        }
        <button type="button" class="btn btn-primary" [disabled]="!fileName() || isProcessing()" (click)="perform()">
          {{ actionLabel() | translate }}
          @if (isProcessing()) { <fa-icon [icon]="faSpinner" animation="spin" class="ml-1"></fa-icon> }
        </button>
      </div>
    </div>
  `,
})
export class CvUploadImportFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private readonly api = inject(ApiService);
  private readonly modal = inject(ModalService);
  private readonly alert = inject(AlertService);
  private readonly studentProfile = inject(StudentProfileService);

  readonly file = signal<File | null>(null);
  readonly fileName = computed(() => this.file()?.name ?? '');
  readonly isImportable = computed(() => isAiImportable(this.file()));
  readonly importEnabled = signal<boolean>(false);
  readonly sections = signal<ImportSections>({ education: true, workExperience: true, languages: true });
  readonly actionLabel = computed(() => actionLabelKey(this.file(), this.importEnabled(), this.sections()));
  readonly isProcessing = signal<boolean>(false);
  readonly error = signal<string>('');

  readonly faSpinner = faSpinner;

  accept = DEFAULT_ACCEPT;
  maxSizeMb = 10;
  /** Optional secondary/cancel button label; only rendered when set (e.g. modal hosts). */
  secondaryLabel = '';

  ngOnInit(): void {
    this.accept = (this.props['accept'] as string) || DEFAULT_ACCEPT;
    this.maxSizeMb = (this.props['maxSizeMb'] as number) ?? 10;
    this.secondaryLabel = (this.props['secondaryLabel'] as string) || '';
  }

  /** Invoke the host-provided secondary action (e.g. close the modal). */
  onSecondary(): void {
    (this.props['onSecondary'] as (() => void) | undefined)?.();
  }

  onFileChange(ev: Event): void {
    const f = (ev.target as HTMLInputElement).files?.[0] ?? null;
    this.error.set('');
    this.file.set(f);
    // Default the AI toggle ON when an importable file (PDF/Word) is chosen, OFF otherwise.
    this.importEnabled.set(isAiImportable(f));
  }

  setSection(key: keyof ImportSections, checked: boolean): void {
    this.sections.update((s) => ({ ...s, [key]: checked }));
  }

  async perform(): Promise<void> {
    const f = this.file();
    if (!f) return;

    if (exceedsSizeMb(f, this.maxSizeMb)) {
      this.error.set(marker('File is too large.'));
      return;
    }

    const profileId = this.props['profileId'] as string | undefined;
    if (!profileId) {
      this.error.set(marker('Configuration error: profileId is missing.'));
      return;
    }

    const doImport = willImport(f, this.importEnabled(), this.sections());

    if (doImport) {
      const ok = await this.modal
        .Confirm(
          marker('Import Profile Data'),
          marker('This will replace your existing data with data from the uploaded file. This action cannot be undone. Are you sure you want to continue?'),
          {}, [], {}, false,
        )
        .catch(() => false);
      if (ok !== true) return;
    }

    this.isProcessing.set(true);
    this.error.set('');

    try {
      // 1. Always upload the raw file (non-destructive).
      try {
        await this.uploadRaw(f, profileId);
      } catch {
        this.error.set(marker('Something went wrong, please try again'));
        return;
      }

      // 2. If applicable, run the AI import (destructive — file is already saved).
      let profile: StudentProfile | undefined;
      if (doImport) {
        try {
          profile = await firstValueFrom(
            this.studentProfile.importProfileUsingDocument({
              profileId,
              body: {
                Document: f,
                Education: this.sections().education,
                WorkExperience: this.sections().workExperience,
                Languages: this.sections().languages,
              },
            }),
          );
        } catch {
          this.error.set(marker('Your CV was saved, but the AI import failed. Please try again.'));
          return;
        }
      }

      this.alert.success(marker('Your CV has been saved'));
      const onComplete = this.props['onComplete'] as ((r: CvUploadImportResult) => void) | undefined;
      onComplete?.({ uploaded: true, imported: doImport, profile });
    } finally {
      this.isProcessing.set(false);
    }
  }

  /** Raw multipart upload to POST /studentprofile/{id}/cv. Rejects on non-200. */
  private uploadRaw(file: File, profileId: string): Promise<void> {
    const url = `${this.api.getApiUrl()}/studentprofile/${profileId}/cv`;
    const headers = this.api.getUploadHeaders();
    const fd = new FormData();
    fd.append('file', file);
    return fetch(url, { method: 'POST', headers, body: fd }).then((res) => {
      if (res.status !== 200) throw new Error('upload-failed');
    });
  }
}
